import settings from "../../constants/settings";
import {
  AttributeValue,
  Context,
  context,
  defaultTextMapSetter,
  propagation,
  ROOT_CONTEXT,
  Span,
  SpanContext,
  SpanStatusCode,
  trace,
} from "@opentelemetry/api";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  StackContextManager,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { track } from "@amplitude/analytics-browser";
import { useCallback } from "react";
import { useAppSelector } from "../hooks/store";
import { W3CTraceContextPropagator } from "@opentelemetry/core";

export const ERROR_KEY = "Unexpected error";
export const tracer = trace.getTracer("flashpcb-dotcom-tracer");

export function anyErrorToError(error: unknown): Error {
  return error instanceof Error ? error : Error(String(error));
}

export function serializeError(error: Error): string {
  return JSON.stringify(error, Object.getOwnPropertyNames(error));
}

export function errorToString(error: unknown): string {
  return serializeError(anyErrorToError(error));
}

export const logApiError = (unknownError: unknown) => {
  // eslint-disable-next-line no-console
  console.log("Error", unknownError);
  const error = anyErrorToError(unknownError);
  tracer.startActiveSpan("Error", (span: Span) => {
    span.setAttribute("gitCommitSha", settings.gitCommitSha);
    span.setAttribute(ERROR_KEY, serializeError(error));
    span.recordException(error);
    span.setStatus({ code: SpanStatusCode.ERROR });
    span.end();
  });
};

export const useSendError = () => {
  const { userId } = useAppSelector((state) => state.auth);

  return useCallback(
    (
      unknownError: unknown,
      options?: {
        additionalAttributes?: { [key: string]: AttributeValue };
        activeSpan?: Span;
      },
    ) => {
      const error = anyErrorToError(unknownError);
      const errorString = serializeError(error);

      const setSpanErrorAttributes = (span: Span) => {
        span.setAttribute(ERROR_KEY, errorString);
        span.setAttribute("userId", userId || "");
        span.setAttribute("gitCommitSha", settings.gitCommitSha);
        span.recordException(error);
        span.setStatus({ code: SpanStatusCode.ERROR });
        if (options?.additionalAttributes) {
          span.setAttributes(options.additionalAttributes);
        }
      };

      track("Errored", {
        error: errorString,
        ...options?.additionalAttributes,
      });
      if (options?.activeSpan) {
        setSpanErrorAttributes(options?.activeSpan);
      } else {
        tracer.startActiveSpan("Error", (span: Span) => {
          setSpanErrorAttributes(span);
          span.end();
        });
      }
    },
    [userId],
  );
};

const exporter = new OTLPTraceExporter({
  url: `${settings.collectorUrl}/v1/traces`,
  headers: settings.collectorHeaders,
});

const provider = new WebTracerProvider({
  resource: resourceFromAttributes({
    ["service.name"]: "flashpcb-dotcom",
  }),
  spanProcessors: [new BatchSpanProcessor(exporter)],
});

provider.register({
  contextManager: new StackContextManager(),
});

export function traceIfParentExists(
  parentSpan: Span | null,
  name: string,
  callback: (span: Span) => unknown,
) {
  if (parentSpan !== null) {
    const parentContext = trace.setSpan(context.active(), parentSpan);
    tracer.startActiveSpan(name, {}, parentContext as Context, callback);
  } else {
    tracer.startActiveSpan(name, callback);
  }
}

export function createLinkingTrace(traceCarrier: Record<string, string>) {
  const extractedCtx = propagation.extract(context.active(), traceCarrier);
  const remoteSpanContext = trace.getSpanContext(extractedCtx) as SpanContext;
  tracer.startActiveSpan(
    "linkTraces",
    {
      links: [{ context: remoteSpanContext }],
    },
    (span) => {
      span.end();
    },
  );
}

export function getTraceCarrierForSpan(span: Span): Record<string, string> {
  const propagator = new W3CTraceContextPropagator();
  const carrier = {} as { [name: string]: string };

  propagator.inject(
    trace.setSpanContext(ROOT_CONTEXT, span.spanContext()),
    carrier,
    defaultTextMapSetter,
  );

  return carrier;
}
